<template>
  <RuesBoxSize
    :size="props.item?.width"
    class="magazin-carousel"
  >
    <template v-if="total > 0">
      <div class="thumb-slider-top flex flex-row justify-start gap-2 w-full">
        <UiCarousel
          ref="categoryCarouselRef"
          class="embla-category w-auto max-w-full m-0"
          :ssr="false"
          :slide-size="100"
          :slide-count="total"
          :show-arrows="false"
          :options="stripOptions"
          :accessibility="categoryAccessibility"
        >
          <div
            v-for="(category, index) in categories"
            :key="category.key"
            class="embla__slide"
          >
            <button
              type="button"
              :class="[
                'px-3 py-4 cursor-pointer border-b-2 font-semibold text-sm',
                activeSlide === index
                  ? 'border-b-petrol-500 text-petrol-500'
                  : 'border-b-neutral-100'
              ]"
              :aria-current="activeSlide === index ? 'true' : undefined"
              @click="scrollTo(index)"
            >
              {{ category.label }}
            </button>
          </div>
        </UiCarousel>
      </div>

      <div class="relative">
        <UiCarousel
          ref="mainCarouselRef"
          class="embla-main h-full w-full"
          :slide-size="100"
          :slide-count="total"
          :show-arrows="false"
          :options="mainOptions"
          :accessibility="mainAccessibility"
          @select="onMainSelect"
        >
          <div
            v-for="category in categories"
            :key="category.key"
            class="embla__slide"
          >
            <UiHeadline
              type="h2"
              design="h2"
              class="mt-10 mb-5"
            >
              {{ category.label }}
            </UiHeadline>
            <div class="grid grid-cols-12 gap-x-5 gap-y-10 md:gap-y-16">
              <a
                v-for="(article, itemKey) in category.articles"
                :key="itemKey"
                :href="article.href"
                target="_blank"
                :rel="article.rel"
                class="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 flex flex-col gap-5 cursor-pointer"
              >
                <img
                  v-if="article?.img_url"
                  :src="article?.img_url"
                  loading="lazy"
                  :alt="'Bild ' + article.title"
                  class="w-full h-[145px] object-cover rounded-xl"
                >
                <div
                  v-else
                  class="w-full h-[145px] object-cover rounded-xl bg-neutral-100"
                />
                <div class="font-bold leading-5">
                  {{ article.title }}
                </div>
              </a>
            </div>
          </div>
        </UiCarousel>

        <div
          v-if="total > 1"
          class="flex justify-center mt-12"
        >
          <div class="thumb-slider relative flex flex-row items-center gap-2">
            <button
              type="button"
              class="embla-nav-button embla-nav-button--prev"
              :aria-label="t('previous_category')"
              :aria-disabled="!canScrollPrev"
              @click.stop.prevent="scrollPrev"
            />
            <UiCarousel
              ref="paginationCarouselRef"
              class="embla-pagination w-auto max-w-[180px]"
              :ssr="false"
              :slide-size="100"
              :slide-count="total"
              :show-arrows="false"
              :options="stripOptions"
              :accessibility="paginationAccessibility"
            >
              <div
                v-for="(category, index) in categories"
                :key="category.key"
                class="embla__slide"
              >
                <button
                  type="button"
                  :class="[
                    'border rounded-full px-2 leading-6 text-center cursor-pointer aspect-1/1',
                    activeSlide === index
                      ? 'border-petrol-500 text-petrol-500'
                      : 'border-transparent hover:text-petrol-500'
                  ]"
                  :aria-label="t('go_to_category', { index: index + 1, label: category.label })"
                  :aria-current="activeSlide === index ? 'true' : undefined"
                  @click="scrollTo(index)"
                >
                  {{ index + 1 }}
                </button>
              </div>
            </UiCarousel>
            <button
              type="button"
              class="embla-nav-button embla-nav-button--next"
              :aria-label="t('next_category')"
              :aria-disabled="!canScrollNext"
              @click.stop.prevent="scrollNext"
            />
          </div>
        </div>
      </div>
    </template>

    <div
      v-else
      class="animate-pulse"
    >
      <div class="col-span-12 w-full max-w-[800px] h-[50px] object-cover rounded-xl bg-neutral-100" />
      <div class="col-span-12 w-full max-w-[300px] h-[30px] object-cover rounded-xl bg-neutral-100 mb-8 mt-10" />
      <div class="grid grid-cols-12 gap-x-5 gap-y-10 md:gap-y-16 w-full overflow-hidden">
        <div
          v-for="n in 8"
          :key="n"
          class="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3"
        >
          <div class="flex flex-col gap-5">
            <div class="w-full h-[145px] object-cover rounded-xl bg-neutral-100" />
            <div class="flex flex-col gap-2">
              <div class="w-full h-[16px] object-cover rounded-xl bg-neutral-100" />
              <div class="w-[80%] h-[16px] object-cover rounded-xl bg-neutral-100" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </RuesBoxSize>
</template>

<script setup lang="ts">
import type { EmblaOptionsType } from 'embla-carousel'
import type { AccessibilityOptionsType } from 'embla-carousel-accessibility'

interface MagazinArticle {
  href?: string
  rel?: string
  img_url?: string
  title?: string
}

interface MagazinItem {
  width?: string | number
  ausstattungen?: Record<string, MagazinArticle[]>
}

interface Props {
  skeleton?: boolean
  item?: MagazinItem | null
}

const props = withDefaults(defineProps<Props>(), {
  skeleton: true,
  item: null
})

const t = useTranslations('RuesMagazinCarousel').translate

const categoryTextMap: Record<string, string> = {
  familienurlaub: t.value('familienurlaub'),
  aktivurlaub: t.value('aktivurlaub'),
  staedtereisen: t.value('staedtereisen'),
  strandurlaub: t.value('strandurlaub'),
  tipps: t.value('tipps'),
  kultururlaub: t.value('kultururlaub'),
  winterurlaub: t.value('winterurlaub'),
  inspiration: t.value('inspiration')
}

const categories = computed(() => {
  const ausstattungen = props.item?.ausstattungen
  if (!ausstattungen) {
    return []
  }
  return Object.entries(ausstattungen).map(([key, articles]) => ({
    key,
    label: categoryTextMap[key] || key,
    articles
  }))
})

const total = computed(() => categories.value.length)

type EmblaCarouselExpose = {
  scrollTo: (index: number, instant?: boolean) => void
  scrollPrev: () => void
  scrollNext: () => void
}

const mainCarouselRef = ref<EmblaCarouselExpose | null>(null)
const categoryCarouselRef = ref<EmblaCarouselExpose | null>(null)
const paginationCarouselRef = ref<EmblaCarouselExpose | null>(null)

const activeSlide = ref(0)

const canScrollPrev = computed(() => activeSlide.value > 0)
const canScrollNext = computed(() => activeSlide.value < total.value - 1)

const mainOptions = computed<EmblaOptionsType>(() => ({
  align: 'start',
  loop: false
}))

const stripOptions = computed<EmblaOptionsType>(() => ({
  align: 'start',
  loop: false,
  dragFree: true,
  containScroll: 'trimSnaps'
}))

const categoryLabelAt = (index: number): string => {
  return categories.value[index]?.label || t.value('category_fallback', { index: index + 1 })
}

const mainAccessibility = computed<AccessibilityOptionsType>(() => ({
  announceChanges: true,
  carouselAriaLabel: t.value('carousel_aria_label'),
  previousButtonAriaLabel: t.value('previous_button_aria_label'),
  nextButtonAriaLabel: t.value('next_button_aria_label'),
  slideAriaLabel: (_grouped, first) => t.value('slide_aria_label', {
    label: categoryLabelAt(first),
    current: first + 1,
    total: total.value
  }),
  liveRegionContent: (_grouped, first) => t.value('live_region_content', {
    label: categoryLabelAt(first),
    current: first + 1,
    total: total.value
  }),
  rootNode: emblaRoot => emblaRoot.parentElement
}))

const categoryAccessibility = computed<AccessibilityOptionsType>(() => ({
  announceChanges: false,
  carouselAriaLabel: t.value('category_nav_aria_label'),
  rootNode: emblaRoot => emblaRoot.parentElement
}))

const paginationAccessibility = computed<AccessibilityOptionsType>(() => ({
  announceChanges: false,
  carouselAriaLabel: t.value('pagination_nav_aria_label'),
  rootNode: emblaRoot => emblaRoot.parentElement
}))

const onMainSelect = (index: number): void => {
  if (activeSlide.value === index) {
    return
  }
  activeSlide.value = index
}

const scrollTo = (index: number): void => {
  activeSlide.value = index
}

const scrollPrev = (): void => {
  if (!canScrollPrev.value) {
    return
  }
  mainCarouselRef.value?.scrollPrev()
}

const scrollNext = (): void => {
  if (!canScrollNext.value) {
    return
  }
  mainCarouselRef.value?.scrollNext()
}

watch(activeSlide, (newIndex) => {
  mainCarouselRef.value?.scrollTo(newIndex, false)
  categoryCarouselRef.value?.scrollTo(newIndex, false)
  paginationCarouselRef.value?.scrollTo(newIndex, false)
})
</script>

<style src="./css/RuesMagazinCarousel.css" scoped></style>
